import React, { useState } from 'react'
import { ExternalLink, Github, Star, Filter } from 'lucide-react'
import { projects } from '../data/portfolio'
import { useScrollAnimation } from '../hooks/useScrollAnimation'
import type { Project } from '../types'

interface ProjectCardProps {
  project: Project
  index: number
}

const ProjectCard: React.FC<ProjectCardProps> = ({ project, index }) => {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 })
  
  return (
    <div 
      ref={ref}
      className={`transition-all duration-700 delay-${index * 100} ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
    >
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-lg border border-gray-200 dark:border-slate-700 overflow-hidden card-hover group">
        {/* Project Image */}
        <div className="relative h-48 bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 overflow-hidden">
          {project.image ? (
            <img 
              src={project.image} 
              alt={project.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white">
              <div className="text-center">
                <div className="text-4xl font-bold mb-2">
                  {project.title.split(' ').map(word => word[0]).join('')}
                </div>
                <div className="text-sm opacity-80">Project Preview</div>
              </div>
            </div>
          )}
          
          {/* Featured badge */}
          {project.featured && (
            <div className="absolute top-3 right-3">
              <div className="flex items-center space-x-1 bg-yellow-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                <Star size={12} fill="currentColor" />
                <span>Featured</span>
              </div>
            </div>
          )}

          {/* Overlay with links */}
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
            <div className="flex space-x-3">
              {(project.link || project.liveUrl) && (
                <a
                  href={project.link || project.liveUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 bg-white text-slate-900 rounded-full hover:bg-gray-100 transition-colors duration-200"
                  aria-label="View live project"
                >
                  <ExternalLink size={18} />
                </a>
              )}
              {project.githubUrl && (
                <a
                  href={project.githubUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 bg-white text-slate-900 rounded-full hover:bg-gray-100 transition-colors duration-200"
                  aria-label="View source code"
                >
                  <Github size={18} />
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Project Content */}
        <div className="p-6">
          <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
            {project.title}
          </h3>
          
          {/* Location and Role */}
          <div className="flex flex-wrap items-center gap-2 mb-3 text-sm text-slate-500 dark:text-slate-400">
            <span>{project.location}</span>
            <span>•</span>
            <span className="font-medium text-blue-600 dark:text-blue-400">{project.role}</span>
            {project.architecture && (
              <>
                <span>•</span>
                <span className="italic">{project.architecture}</span>
              </>
            )}
          </div>
          
          <p className="text-slate-600 dark:text-slate-300 mb-4 leading-relaxed">
            {project.description}
          </p>

          {/* Long description for featured projects */}
          {project.featured && project.longDescription && (
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4 leading-relaxed">
              {project.longDescription}
            </p>
          )}

          {/* Technologies */}
          <div className="mb-4">
            <div className="flex flex-wrap gap-2">
              {project.technologies.slice(0, 4).map((tech, idx) => (
                <span 
                  key={idx}
                  className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 text-xs rounded-md font-medium"
                >
                  {tech}
                </span>
              ))}
              {project.technologies.length > 4 && (
                <span className="px-2 py-1 bg-gray-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 text-xs rounded-md font-medium">
                  +{project.technologies.length - 4} more
                </span>
              )}
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex space-x-3">
            {(project.link || project.liveUrl) && (
              <a
                href={project.link || project.liveUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 btn-primary text-center text-sm py-2"
              >
                View Live
              </a>
            )}
            {project.githubUrl && (
              <a
                href={project.githubUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 btn-secondary text-center text-sm py-2"
              >
                Source Code
              </a>
            )}
            {!project.link && !project.liveUrl && !project.githubUrl && (
              <div className="flex-1 text-center text-sm py-2 text-slate-500 dark:text-slate-400">
                Private Project
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

const Projects: React.FC = () => {
  const { ref: titleRef, isVisible: titleVisible } = useScrollAnimation()
  const [selectedFilter, setSelectedFilter] = useState<string>('all')

  // Extract unique technologies for filter
  const allTechnologies = Array.from(
    new Set(projects.flatMap(project => project.technologies))
  ).sort()

  const filterOptions = [
    { value: 'all', label: 'All Projects' },
    { value: 'featured', label: 'Featured' },
    ...allTechnologies.map(tech => ({ value: tech, label: tech }))
  ]

  const filteredProjects = projects.filter(project => {
    if (selectedFilter === 'all') return true
    if (selectedFilter === 'featured') return project.featured
    return project.technologies.includes(selectedFilter)
  })

  return (
    <section id="projects" className="section-padding bg-gray-50 dark:bg-slate-800">
      <div className="container-max">
        <div 
          ref={titleRef}
          className={`text-center mb-16 transition-all duration-700 ${
            titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-slate-900 dark:text-white">
            Featured Projects
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
            A showcase of my work in AI applications, backend systems, and full-stack development
          </p>
        </div>

        {/* Filter Section */}
        <div className="mb-12">
          <div className="flex items-center justify-center mb-6">
            <div className="flex items-center space-x-2 text-slate-600 dark:text-slate-300">
              <Filter size={18} />
              <span className="font-medium">Filter by:</span>
            </div>
          </div>
          
          <div className="flex flex-wrap justify-center gap-2">
            {filterOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => setSelectedFilter(option.value)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  selectedFilter === option.value
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-blue-50 dark:hover:bg-slate-600 border border-gray-200 dark:border-slate-600'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Projects Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProjects.map((project, index) => (
            <ProjectCard 
              key={project.id}
              project={project}
              index={index}
            />
          ))}
        </div>


        {/* No projects message */}
        {filteredProjects.length === 0 && (
          <div className="text-center py-12">
            <p className="text-slate-500 dark:text-slate-400 text-lg">
              No projects found for the selected filter.
            </p>
            <button
              onClick={() => setSelectedFilter('all')}
              className="mt-4 btn-primary"
            >
              Show All Projects
            </button>
          </div>
        )}

        {/* Call to action */}
        <div className="text-center mt-16">
          <div className="bg-white dark:bg-slate-900 p-8 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm">
            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
              Have a project in mind?
            </h3>
            <p className="text-slate-600 dark:text-slate-300 mb-4">
              I'm always interested in discussing new opportunities and challenging projects.
            </p>
            <a 
              href="#contact" 
              className="btn-primary inline-block"
            >
              Let's Collaborate
            </a>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Projects